/**
 * What a dish row on the table board decides.
 *
 * The rule worth naming: a dish is workable only once the waiter has actually
 * called its wave into the kitchen (`firedAt`). Until then the row is visible
 * but grey and has no "Inizia" button — the food is not the kitchen's yet, and
 * starting it early produces a plate that stands under the lamp.
 */
import type { DelayTier } from "@shared/kds/table-board";
import type { KdsT } from "./i18n/settings";

export const DISH_ROW_STATUS_DOT: Record<string, string> = {
  PENDING: "bg-kds-pending",
  CONFIRMED: "bg-kds-pending",
  IN_PROGRESS: "bg-kds-preparing",
  PREPARING: "bg-kds-preparing",
  READY: "bg-kds-ready",
  SERVED: "bg-kds-served",
  CANCELLED: "bg-kds-cancelled",
};

export const DISH_ROW_STATUS_LABEL: Record<string, (t: KdsT) => string> = {
  PENDING: (t) => t("kds.status.toDo", "Da fare"),
  CONFIRMED: (t) => t("kds.status.toDo", "Da fare"),
  IN_PROGRESS: (t) => t("kds.status.inPrep", "In prep."),
  PREPARING: (t) => t("kds.status.inPrep", "In prep."),
  READY: (t) => t("kds.status.ready", "Pronto"),
  SERVED: (t) => t("kds.status.served", "Servito"),
  CANCELLED: (t) => t("kds.status.cancelled", "Annullato"),
};

/** Without a translator the label is the Italian source text. */
const sourceText: KdsT = (_key, fallback) => fallback;

/** The chef stops at READY; the waiter says SERVED when the plate lands. */
export const DISH_ROW_STATUS_CYCLE: Record<string, string> = {
  PENDING: "PREPARING",
  PREPARING: "READY",
};

export const DELAY_TIER_CLASS: Record<DelayTier, string> = {
  normal: "",
  warn: "border-l-2 border-yellow-400 bg-yellow-400/5",
  overdue: "border-l-2 border-red-500 bg-red-500/10 animate-pulse",
};

/**
 * Whether the dish is something the bar has to make, as opposed to something it
 * takes out of a fridge. A bottle gets one "Servito" button; a cocktail keeps
 * the full cycle.
 */
export function dishNeedsPreparation(item: any): boolean {
  const recipe = item?.dish?.recipe;
  if (!recipe || recipe.isActive === false) return false;
  return Array.isArray(recipe.items) && recipe.items.length > 0;
}

/** Whole seconds since an ISO timestamp, null-safe. */
export function secsSince(ts: string | null | undefined, now: number): number | null {
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

/** Seconds as "mm:ss", or "h:mm:ss" past the hour. */
export function fmtClock(totalSecs: number | null): string {
  if (totalSecs === null) return "—";
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export interface DishRowState {
  /** The waiter has sent this wave into the kitchen. */
  isFired: boolean;
  /** Waiting on the waiter, not on the cook. */
  isPendingUnfired: boolean;
  canAdvance: boolean;
  canServe: boolean;
  /** Bar board, nothing to prepare: the only action is "Servito". */
  serveOnly: boolean;
  dotClass: string;
  label: string;
  delayClass: string;
}

export function dishRowState({
  item,
  served,
  bar,
  delayTier,
  t = sourceText,
}: {
  item: any;
  served: boolean;
  bar: boolean;
  delayTier: DelayTier;
  /** The component's translator, for `label`. */
  t?: KdsT;
}): DishRowState {
  const serveOnly = bar && !dishNeedsPreparation(item);
  const isFired = !!item?.firedAt;
  const isPendingUnfired =
    !served && !isFired && (item?.status === "PENDING" || item?.status === "CONFIRMED");

  return {
    isFired,
    isPendingUnfired,
    serveOnly,
    canAdvance: !served && !serveOnly && isFired && item?.status in DISH_ROW_STATUS_CYCLE,
    canServe:
      serveOnly &&
      !served &&
      isFired &&
      item?.status !== "SERVED" &&
      item?.status !== "CANCELLED",
    dotClass: DISH_ROW_STATUS_DOT[item?.status] ?? "bg-kds-muted",
    label: DISH_ROW_STATUS_LABEL[item?.status]?.(t) ?? item?.status,
    // A served row is a preview, not something to act on.
    delayClass: served ? "" : DELAY_TIER_CLASS[delayTier],
  };
}
